import { useState } from "react";
import { motion } from "framer-motion";

function SubscribeForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Invalid email format');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Starting Klaviyo submission for email:', email);
      console.log('Klaviyo object available:', !!window.klaviyo);

      // Since there are network connectivity issues with Klaviyo domains,
      // let's use a hidden iframe approach to submit to Klaviyo
      console.log('Using iframe submission method...');

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.name = 'klaviyo_submit';
      document.body.appendChild(iframe);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://manage.kmail-lists.com/subscriptions/subscribe';
      form.target = 'klaviyo_submit';

      // Add form fields
      const fields = {
        'g': 'XZUhbf',
        'email': email,
        '$source': 'landing_page_form'
      };

      Object.keys(fields).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = fields[key];
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(form);
        document.body.removeChild(iframe);
      }, 1000);

      console.log('Form submitted via iframe');

      // Also try the onsite tracking if available
      if (window.klaviyo && window.klaviyo.push) {
        console.log('Also attempting onsite tracking...');
        try {
          window.klaviyo.push(['identify', {
            '$email': email,
            '$source': 'landing_page_form'
          }]);

          window.klaviyo.push(['track', 'Newsletter Signup', {
            '$email': email,
            'source': 'landing_page'
          }]);
          console.log('Onsite tracking completed');
        } catch (onsiteError) {
          console.warn('Onsite tracking failed:', onsiteError);
        }
      }

      setIsSubmitting(false);
      setIsSuccess(true);
      setEmail('');
    } catch (error) {
      console.error('Klaviyo submission error:', error);
      setIsSubmitting(false);
      setError('Something went wrong. Please try again.');
    }
  };

  if (isSuccess) {
    return (
      <div className="w-full max-w-xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-white tracking-widest"
          role="status"
          aria-live="polite"
        >
          <h2 className="text-lg mb-2 font-medium">TRANSMISSION RECEIVED</h2>
          <p className="text-sm opacity-70">You will be notified when clearance is granted.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto text-center">
      <h2 className="text-white tracking-widest mb-3 text-lg font-medium">Subscribe for Updates & Announcements</h2>
      <form onSubmit={handleSubmit} role="form" aria-label="Newsletter subscription form">
        <div className="flex flex-col sm:flex-row justify-center items-center gap-2 mb-4">
          <label htmlFor="email-input" className="sr-only">Email address</label>
          <input 
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ENTER EMAIL" 
            className={`border w-full max-w-sm bg-black text-white px-4 py-2 tracking-widest placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black transition-all ${
              error ? 'border-red-500 focus:ring-red-500' : 'border-white'
            }`}
            disabled={isSubmitting}
            required
            aria-describedby={error ? "email-error" : undefined}
            aria-invalid={error ? "true" : "false"}
          />
          <button 
            type="submit"
            disabled={isSubmitting}
            className="border border-white bg-white text-black px-6 py-2 tracking-widest uppercase hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
            aria-describedby="submit-status"
          >
            <span id="submit-status" className="sr-only">{isSubmitting ? 'Submitting form' : 'Submit form'}</span>
            {isSubmitting ? 'SENDING...' : 'SUBMIT'}
          </button>
        </div>
        {error && (
          <motion.p 
            id="email-error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-400 text-sm tracking-widest text-center mt-2"
            role="alert"
            aria-live="polite"
          >
            <span className="sr-only">Error: </span>{error}
          </motion.p>
        )}
      </form>
    </div>
  );
}

export default SubscribeForm;